import { FC, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useAppDispatch } from '@/core/store/hooks'
import { cn } from '@bem-react/classname'
import { yupResolver } from '@hookform/resolvers/yup'

import { useHandleServerError } from '@/hooks/useHandleServerError'

import { Button } from '@/components/UI/Button'
import { Card } from '@/components/UI/Card'
import Icon from '@/components/UI/Icon'
import { Logo } from '@/components/UI/Logo'

import { EmailCodeStep } from './components/EmailCodeStep'
import { EmailStep } from './components/EmailStep'
import { FinishStep } from './components/FinishStep'
import { PhoneCodeStep } from './components/PhoneCodeStep'
import { PhoneStep } from './components/PhoneStep'
import { PoliciesStep } from './components/PoliciesStep'
import {
  RegisterFormData,
  RegisterStep,
  RegisterValidationCodeEmailSchema,
  RegisterValidationCodePhoneSchema,
  RegisterValidationEmailSchema,
  RegisterValidationFinishSchema,
  RegisterValidationPhoneSchema,
  RegisterValidationPoliciesSchema,
} from './models/Register.model'
import {
  checkPoliciesThunk,
  emailVerifyThunk,
  enterPhoneThunk,
  registerFinishThunk,
  registrationThunk,
  smsVerifyThunk,
} from './store/AuthSlice'
import './styles/Register.scss'

const cnRegister = cn('Register')

export const Register: FC = () => {
  const { t, ready } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { formErrors, handleServerError } =
    useHandleServerError<keyof RegisterFormData>()

  const [form, setForm] = useState<RegisterFormData | null>(null)
  const [step, setStep] = useState<number>(0)
  const [btnFirst, setBtnFirst] = useState<string>('')
  const [btnSecond, setBtnSecond] = useState<string>('')

  const onCompleted = () => {
    methods.handleSubmit(onRegister)()
  }

  const steps: RegisterStep[] = [
    {
      component: <EmailStep />,
      schema: RegisterValidationEmailSchema,
      defaultValues: { email: '' },
    },
    {
      component: <PoliciesStep />,
      schema: RegisterValidationPoliciesSchema,

      defaultValues: {
        conditions: false,
        concept: false,
        policy: false,
      },
    },
    {
      component: (
        <EmailCodeStep value={form?.email} onCompleted={onCompleted} />
      ),
      schema: RegisterValidationCodeEmailSchema,
      defaultValues: {
        email_code: '',
      },
    },

    {
      component: <PhoneStep />,
      schema: RegisterValidationPhoneSchema,
      defaultValues: { phone: '' },
    },

    {
      component: (
        <PhoneCodeStep value={form?.phone} onCompleted={onCompleted} />
      ),
      schema: RegisterValidationCodePhoneSchema,
      defaultValues: {
        phone_code: '',
      },
    },

    {
      component: <FinishStep />,
      schema: RegisterValidationFinishSchema,
      defaultValues: {
        confirm: false,
      },
    },
  ]

  const methods = useForm<RegisterFormData>({
    resolver: yupResolver(steps[step].schema),
    mode: 'onChange',
    defaultValues: steps[step].defaultValues,
  })

  console.log(methods.formState.errors)

  const onRegister = async (data: RegisterFormData) => {
    setForm({ ...form, ...data })

    try {
      switch (step) {
        case 0:
          {
            const res = await dispatch(registrationThunk(data)).unwrap()
            setStep(1)
            methods.setValue('token', res.token)
          }

          break

        case 1:
          {
            await dispatch(
              checkPoliciesThunk({ token: data.token, agree: 1 }),
            ).unwrap()
            setStep(2)
          }

          break

        case 2:
          {
            await dispatch(
              emailVerifyThunk({ token: data.token, code: data.email_code }),
            ).unwrap()
            setStep(3)
          }

          break

        case 3:
          {
            await dispatch(
              enterPhoneThunk({ token: data.token, phone: `+${data.phone}` }),
            ).unwrap()
            setStep(4)
          }

          break

        case 4:
          {
            await dispatch(
              smsVerifyThunk({ token: data.token, code: data.phone_code }),
            ).unwrap()
            setStep(5)
          }

          break

        default:
          {
            const res = await dispatch(
              registerFinishThunk({ token: data.token }),
            ).unwrap()

            if (res.token) {
              navigate('/c/verify')
              localStorage.setItem('token', res.token)
            }
          }
          break
      }
    } catch (error) {
      handleServerError(error)
    }
  }

  const onLeaveForLater = async () => {
    const values = methods.getValues()

    try {
      const res = await dispatch(
        registerFinishThunk({ token: values.token }),
      ).unwrap()

      if (res.token) {
        navigate('/c')
        localStorage.setItem('token', res.token)
      }
    } catch (error) {
      handleServerError(error)
    }
  }

  useEffect(() => {
    if (formErrors) {
      Object.entries(formErrors).forEach(([key, message]) => {
        methods.setError(key as keyof RegisterFormData, {
          type: 'manual',
          message,
        })
      })
    }
  }, [formErrors])

  useEffect(() => {
    methods.setValue('type', 'person')
  }, [step])

  useEffect(() => {
    switch (step) {
      case 0:
        setBtnFirst(t('register-continue'))
        setBtnSecond(t('register-login'))
        break
      case 1:
        setBtnSecond(t('register-back'))
        break
      case 5:
        setBtnFirst(t('register-get-started'))
        setBtnSecond(t('register-leave-for-later'))
        break
      default:
        setBtnSecond(t('register-login'))
        break
    }
  }, [step, ready])

  const onNavigate = () => {
    switch (step) {
      case 1:
        setStep(0)
        break
      case 5:
        onLeaveForLater()
        break
      default:
        navigate('/login')
        break
    }
  }

  return (
    <FormProvider {...methods}>
      <form
        className={cnRegister()}
        onSubmit={methods.handleSubmit(onRegister)}
      >
        <Card className={cnRegister('card')}>
          <Logo to='/' className={cnRegister('logo')} />
          {steps[step].component}

          {step !== 2 && step !== 4 && (
            <div className={cnRegister('actions')}>
              <Button
                iconPosition='right'
                type='submit'
                isLoading={methods.formState.isSubmitting}
              >
                {btnFirst}
                <Icon name='chevron-right' stroke='white' />
              </Button>

              {step !== 3 && (
                <Button color='blue' onClick={onNavigate}>
                  {btnSecond}
                </Button>
              )}
            </div>
          )}
        </Card>
      </form>
    </FormProvider>
  )
}
